import {
  createStyles,
  Theme,
  type WithStyles,
  withStyles,
} from "@material-ui/core/styles";
import { useTranslations } from "adapters/i18n";
import SelectDropdown from "components/SelectDropdown";
import SkeletonLoading from "features/shared/components/SkeletonLoading";
import TabelaProdap from "features/shared/components/TabelaProdap";
import React from "react";
import type { FarmAndRetreatLink } from "../../../../userManagement.types";
import { useFarmAndRetreatsLinks } from "../hooks/useFarmAndRetreatsLinks";
import { useFarmAndRetreatsOptions } from "../hooks/useFarmAndRetreatsOptions";
import { useLinksTableColumns } from "../hooks/useLinksTableColumns";
import type { UserFormState } from "../userForm.types";

type FarmAndRetreatLinkFormProps = {
  formData: UserFormState["farmAndRetreatLinks"];
  onAddLink: (link: FarmAndRetreatLink) => void;
  onRemoveLink: (farmId: number) => void;
} & WithStyles<typeof styles>;

function FarmAndRetreatLinkForm({
  classes,
  formData,
  onAddLink,
  onRemoveLink,
}: FarmAndRetreatLinkFormProps) {
  const { t } = useTranslations();

  const {
    farmAndRetreatsForm,
    handleSelectItem,
    handleClearSelection,
    toggleFormError,
  } = useFarmAndRetreatsLinks();

  const { farmOptions, isFetchingOptions, retreatOptions } =
    useFarmAndRetreatsOptions({
      formData: formData.value,
      selectedFarmId: farmAndRetreatsForm.farm.value,
    });

  const handleAddLink = () => {
    if (
      farmAndRetreatsForm.farm.value == null ||
      farmAndRetreatsForm.retreats.value.length === 0
    ) {
      toggleFormError();
      return;
    }

    const selectedFarm = farmOptions.find(
      (farm) => farm.value === farmAndRetreatsForm.farm.value
    );
    const selectedRetreats = retreatOptions.filter((retreat) =>
      farmAndRetreatsForm.retreats.value.includes(retreat.value)
    );

    if (selectedFarm == null || selectedRetreats.length === 0) {
      toggleFormError();
      return;
    }

    onAddLink({
      farm: { id: selectedFarm.value, name: selectedFarm.label },
      retreats: selectedRetreats.map((retreat) => ({
        id: retreat.value,
        name: retreat.label,
      })),
    });
    handleClearSelection();
  };

  const handleRemoveLink = (farmId: number) => {
    onRemoveLink(farmId);
  };

  const { columns } = useLinksTableColumns({ onRemoveLink: handleRemoveLink });

  const retreatsNoOptionsLabel =
    farmAndRetreatsForm.farm.value != null
      ? t("locales:no-retreat-available")
      : t("locales:select-a-farm");

  if (isFetchingOptions) {
    return (
      <div data-testid="farm-and-retreat-skeleton">
        <SkeletonLoading width={310} height={24} />
        <SkeletonLoading width={769} height={53} marginTop={24} />
      </div>
    );
  }

  return (
    <div>
      <span className={classes.title}>
        {t("locales:select-farms-for-user-access")}
      </span>

      <div className={classes.selectContainer}>
        <SelectDropdown
          error={formData.error || farmAndRetreatsForm.farm.error}
          isSearchable={true}
          label={{ text: t("locales:farm") }}
          onChange={handleSelectItem("farm")}
          options={farmOptions}
          styles={{ width: "300px" }}
          value={farmAndRetreatsForm.farm.value}
        />
        <SelectDropdown
          isSearchable={true}
          isMulti={true}
          label={{ text: t("locales:retreats") }}
          overrideStrings={{ noOptions: retreatsNoOptionsLabel }}
          styles={{ maxWidth: "500px" }}
          options={retreatOptions}
          value={farmAndRetreatsForm.retreats.value}
          onChange={handleSelectItem("retreats")}
          error={formData.error || farmAndRetreatsForm.retreats.error}
        />
        <button
          className={classes.addButton}
          data-testid="add-link-button"
          onClick={handleAddLink}
        >
          {t("common:actions.add")}
        </button>
      </div>

      {formData.value.length > 0 && (
        <div className={classes.tableContainer}>
          <TabelaProdap
            tabelaEsquerda={true}
            ativarPaginacao={false}
            modoOrdenacao="CLIENTE"
            colunas={columns}
            linhas={formData.value}
          />
        </div>
      )}
    </div>
  );
}

const styles = (theme: Theme) =>
  createStyles({
    title: {
      fontFamily: "Roboto",
      fontWeight: 700,
      fontSize: "20px",
      color: "#444444",
    },
    selectContainer: {
      marginTop: "24px",
      display: "flex",
      gap: "32px",
      alignItems: "center",
    },
    addButton: {
      fontFamily: "Roboto",
      fontSize: "14px",
      fontWeight: 500,
      color: "#ffffff",
      backgroundColor: theme.palette.primary.main,
      padding: "12px 16px",
      borderRadius: "18px",
      border: "none",
      cursor: "pointer",
      textTransform: "uppercase",
    },
    tableContainer: {
      marginTop: "40px",
    },
  });

export default withStyles(styles)(FarmAndRetreatLinkForm);
